import { useEffect, useMemo, useState } from "react";
import { Command } from "cmdk";
import * as Dialog from "@radix-ui/react-dialog";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Inbox, CircleUserRound, Settings, Moon, Sun, Monitor, Languages, SquarePen, FolderPlus, CircleDot, FileText, Search, LoaderCircle } from "lucide-react";
import { ProjectIcon, type ProjectAppearance } from "./ProjectIcon";
import { applyTheme } from "@/lib/theme";
import { setLocale } from "@/i18n";
import { api, errorMessage, updateMe } from "@/lib/api";
import { liveIssues, liveMilestones, liveTasks, useProjectStore } from "@/lib/store";
import { TaskStatusIcon } from "./TaskProps";
import { Flag } from "lucide-react";
import { StateIcon } from "./PropertyIcons";

import { unwrap } from "@tern/api-client";

const route = getRouteApi("/$ws");

/** ⌘K navigation and permission-aware search across the workspace. */
export function CommandPalette({
  open,
  onOpenChange,
  projects,
  onNewIssue,
  onNewProject,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  projects: (ProjectAppearance & { id: string; key: string; name: string })[];
  onNewIssue: () => void;
  onNewProject: () => void;
}) {
  const { workspace } = route.useRouteContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const persist = useMutation({ mutationFn: updateMe });
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [kind, setKind] = useState<"all" | "doc" | "task" | "issue">("all");
  useEffect(() => { const timer = setTimeout(() => setDebounced(query.trim()), 250); return () => clearTimeout(timer); }, [query]);
  const searching = query.trim().length > 0;
  const results = useQuery({
    queryKey: ["workspace-search", workspace.slug, debounced, kind],
    queryFn: async ({ signal }) => unwrap(await api.GET("/api/workspaces/{slug}/search", { params: { path: { slug: workspace.slug }, query: { q: debounced, kind: kind === "all" ? undefined : kind } }, signal })),
    enabled: open && !!debounced,
    staleTime: 0,
  });
  const waiting = query.trim() !== debounced || results.isFetching;
  const loaded = useProjectStore((s) => s.byId);
  const issues = useMemo(() => Object.values(loaded).flatMap((d) => liveIssues(d).map((i) => ({ d, i }))).slice(0, 2000), [loaded]);
  const tasks = useMemo(() => Object.values(loaded).flatMap((d) => liveTasks(d).map((tk) => ({ d, tk }))), [loaded]);
  const milestones = useMemo(() => Object.values(loaded).flatMap((d) => liveMilestones(d).map((m) => ({ d, m }))), [loaded]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  const go = (to: string) => {
    onOpenChange(false);
    void navigate({ to });
  };
  const run = (fn: () => void) => () => {
    fn();
    onOpenChange(false);
  };
  const item = "flex h-9 cursor-default items-center gap-2.5 rounded-md px-2.5 text-[13px] text-fg data-[selected=true]:bg-hover";
  const group = "[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-faint";

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-[18%] z-50 w-[600px] max-w-[calc(100vw-32px)] -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-elevated shadow-2xl">
          <Dialog.Title className="sr-only">{t("common.search")}</Dialog.Title>
          <Command label={t("common.search")} loop shouldFilter={!searching}>
            <Command.Input value={query} onValueChange={setQuery} placeholder={t("palette.placeholder")} className="h-12 w-full border-b border-border bg-transparent px-4 text-[14px] text-fg outline-none placeholder:text-faint" autoFocus />
            {searching && <div className="flex items-center gap-1 border-b border-border px-3 py-2">{(["all", "doc", "task", "issue"] as const).map((value) => <button key={value} className={`rounded-md px-2.5 py-1 text-[12px] ${kind === value ? "bg-active text-fg" : "text-muted hover:bg-hover"}`} onClick={() => setKind(value)}>{t(`search.kind.${value}`)}</button>)}<span className="ml-auto flex items-center gap-1.5 text-[11px] text-faint">{waiting ? <LoaderCircle className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3" />}{t(results.data?.mode === "hybrid" ? "search.hybrid" : "search.keyword")}</span></div>}
            <Command.List className="max-h-[460px] overflow-y-auto p-1.5">
              {searching ? <>
                {results.error && <p role="alert" className="px-4 py-5 text-[13px] text-danger">{errorMessage(results.error)}</p>}
                {!waiting && !results.error && !results.data?.results.length && <div className="px-4 py-8 text-center text-[13px] text-muted">{t("common.noResults")}</div>}
                {results.data?.results.map((hit) => <Command.Item key={`${hit.entity_type}:${hit.entity_id}`} value={`${hit.entity_type}:${hit.entity_id}`} className="flex cursor-default items-start gap-3 rounded-lg px-3 py-3 data-[selected=true]:bg-hover" onSelect={() => go(hit.url)}>
                  {hit.entity_type === "doc" ? <FileText className="mt-0.5 h-4 w-4 shrink-0 text-muted" /> : hit.entity_type === "task" ? <FolderPlus className="mt-0.5 h-4 w-4 shrink-0 text-muted" /> : <CircleDot className="mt-0.5 h-4 w-4 shrink-0 text-muted" />}
                  <div className="min-w-0 flex-1"><div className="flex items-center gap-2 text-[13px] font-medium">{hit.identifier && <span className="shrink-0 font-mono text-[11px] text-muted">{hit.identifier}</span>}<span className="truncate">{hit.title}</span></div><p className="mt-1 line-clamp-2 text-[12px] leading-5 text-muted">{hit.excerpt}</p><p className="mt-1 text-[10px] text-faint">{projects.find((p) => p.id === hit.project_id)?.name} · {t(`search.kind.${hit.entity_type}`)}</p></div>
                </Command.Item>)}
                {results.data?.notice && <p className="px-3 py-2 text-[11px] leading-5 text-muted">{t("search.semanticUnavailable")}</p>}
              </> : <>
              <Command.Empty className="px-3 py-6 text-center text-[13px] text-muted">{t("common.noResults")}</Command.Empty>
              <Command.Group heading={t("palette.actions")} className={group}>
                <Command.Item className={item} onSelect={run(onNewIssue)}><SquarePen className="h-4 w-4 text-muted" /> {t("nav.newIssue")} <Kbd>C</Kbd></Command.Item>
                <Command.Item className={item} onSelect={run(onNewProject)}><FolderPlus className="h-4 w-4 text-muted" /> {t("nav.newProject")}</Command.Item>
              </Command.Group>
              <Command.Group heading={t("palette.navigate")} className={group}>
                <Command.Item className={item} onSelect={() => go(`/${workspace.slug}/inbox`)}><Inbox className="h-4 w-4 text-muted" /> {t("nav.inbox")} <Kbd>G I</Kbd></Command.Item>
                <Command.Item className={item} onSelect={() => go(`/${workspace.slug}/my-issues/assigned`)}><CircleUserRound className="h-4 w-4 text-muted" /> {t("nav.myIssues")} <Kbd>G M</Kbd></Command.Item>
                <Command.Item className={item} onSelect={() => go(`/${workspace.slug}/settings`)}><Settings className="h-4 w-4 text-muted" /> {t("nav.settings")} <Kbd>G S</Kbd></Command.Item>
                {projects.map((p) => (
                  <Command.Item key={p.id} className={item} value={`${p.name} ${p.key} project`} onSelect={() => go(`/${workspace.slug}/project/${p.key}`)}>
                    <ProjectIcon project={p} size={16} /> {p.name} <span className="text-faint">{p.key}</span>
                  </Command.Item>
                ))}
              </Command.Group>
              {issues.length > 0 && (
                <Command.Group heading={t("nav.issues")} className={group}>
                  {issues.map(({ d, i }) => (
                    <Command.Item key={i.id} className={item} value={`${d.project.key}-${i.number} ${i.title}`} onSelect={() => go(`/${workspace.slug}/issue/${d.project.key}-${i.number}`)}>
                      <StateIcon state={d.states[i.state_id]} className="h-3.5 w-3.5" />
                      <span className="font-mono text-[11.5px] text-muted">{d.project.key}-{i.number}</span>
                      <span className="truncate">{i.title}</span>
                    </Command.Item>
                  ))}
                </Command.Group>
              )}
              {tasks.length > 0 && (
                <Command.Group heading={t("nav.tasks")} className={group}>
                  {tasks.map(({ d, tk }) => (
                    <Command.Item key={tk.id} className={item} value={`${tk.name} task ${d.project.key}`} onSelect={() => go(`/${workspace.slug}/task/${tk.id}`)}>
                      <TaskStatusIcon status={tk.status} />
                      <span className="truncate">{tk.name}</span>
                      <span className="ml-auto text-[11px] text-faint">{d.project.key}</span>
                    </Command.Item>
                  ))}
                </Command.Group>
              )}
              {milestones.length > 0 && (
                <Command.Group heading={t("nav.milestone")} className={group}>
                  {milestones.map(({ d, m }) => (
                    <Command.Item key={m.id} className={item} value={`${m.name} milestone ${d.project.key}`} onSelect={() => go(`/${workspace.slug}/project/${d.project.key}/milestones`)}>
                      <Flag className="h-3.5 w-3.5 text-muted" />
                      <span className="truncate">{m.name}</span>
                      <span className="ml-auto text-[11px] text-faint">{m.target_date}</span>
                    </Command.Item>
                  ))}
                </Command.Group>
              )}
              <Command.Group heading={t("palette.preferences")} className={group}>
                <Command.Item className={item} onSelect={run(() => { applyTheme("dark"); persist.mutate({ theme: "dark" }); })}><Moon className="h-4 w-4 text-muted" /> {t("palette.theme.label")}: {t("palette.theme.dark")}</Command.Item>
                <Command.Item className={item} onSelect={run(() => { applyTheme("light"); persist.mutate({ theme: "light" }); })}><Sun className="h-4 w-4 text-muted" /> {t("palette.theme.label")}: {t("palette.theme.light")}</Command.Item>
                <Command.Item className={item} onSelect={run(() => { applyTheme("system"); persist.mutate({ theme: "system" }); })}><Monitor className="h-4 w-4 text-muted" /> {t("palette.theme.label")}: {t("palette.theme.system")}</Command.Item>
                <Command.Item className={item} onSelect={run(() => { setLocale("en"); persist.mutate({ locale: "en" }); })}><Languages className="h-4 w-4 text-muted" /> {t("palette.language.label")}: English</Command.Item>
                <Command.Item className={item} onSelect={run(() => { setLocale("ko"); persist.mutate({ locale: "ko" }); })}><Languages className="h-4 w-4 text-muted" /> {t("palette.language.label")}: 한국어</Command.Item>
              </Command.Group>
              <Command.Group heading={t("nav.help")} className={group}>
                <Command.Item className={`${item} pointer-events-none flex-wrap gap-y-1 text-[12px] text-muted`} value="shortcuts help keyboard">
                  <CircleDot className="h-3.5 w-3.5" /> J/K {t("help.move")} · ↵ {t("help.open")} · X {t("help.select")} · S {t("issue.status")} · P {t("issue.priority")} · A {t("issue.assignee")} · L {t("issue.labels")} · ⌘B {t("help.toggleLayout")}
                </Command.Item>
              </Command.Group>
              </>}
            </Command.List>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Kbd({ children }: { children: string }) {
  return (
    <span className="ml-auto flex gap-1">
      {children.split(" ").map((k) => (
        <kbd key={k} className="kbd">{k}</kbd>
      ))}
    </span>
  );
}
